import { ArrowRight } from "lucide-react";
import { consumerLink } from "../lib/links";

type Lang = "nl" | "en";

const COPY: Record<
  Lang,
  {
    imageAlt: string;
    heading: string;
    text: string;
    button: string;
    note: string;
  }
> = {
  nl: {
    imageAlt: "Kopers berekenen hun bod op een Nederlandse woning",
    heading: "Twijfel je over je bod?",
    text: "Biedradar analyseert vergelijkbare verkopen, overbiedstatistieken, WOZ-taxatierisico en je hypotheekruimte — zodat je biedt met data, niet met je onderbuik.",
    button: "Analyseer jouw woning gratis",
    note: "Geen account nodig · 5 gratis rapporten per dag",
  },
  en: {
    imageAlt: "Buyers calculating their bid on a Dutch home",
    heading: "Unsure about your bid?",
    text: "Biedradar analyses comparable sales, overbidding statistics, WOZ appraisal risk and your borrowing room — so you bid with data, not on gut feeling.",
    button: "Analyse your home for free",
    note: "No account needed · 5 free reports per day",
  },
};

interface BlogCTAProps {
  /** Image from /public. Defaults to the starters photo. */
  image?: string;
  imageAlt?: string;
  heading?: string;
  text?: string;
  buttonLabel?: string;
  lang?: Lang;
}

/**
 * Conversion CTA for blog/guide pages — funnels readers into the report tool.
 * Pro design language: rounded card, soft shadow, brand fill button.
 * Copy follows `lang` (NL/EN); individual strings can still be overridden.
 *
 * Ported into the Pro app: the report tool lives on the consumer site, so the
 * button is an external link to biedradar.nl rather than an in-app route.
 */
export function BlogCTA({
  lang = "nl",
  image = "/neo-hypotheken-starters-eerste-huis-kopen.jpg",
  imageAlt,
  heading,
  text,
  buttonLabel,
}: BlogCTAProps) {
  const t = COPY[lang];
  imageAlt = imageAlt ?? t.imageAlt;
  heading = heading ?? t.heading;
  text = text ?? t.text;
  buttonLabel = buttonLabel ?? t.button;
  return (
    <aside className="my-12 overflow-hidden rounded-xl border border-line bg-surface-2 shadow-soft">
      <div className="grid md:grid-cols-2">
        <div className="min-h-[220px] md:min-h-full">
          <img
            src={image}
            alt={imageAlt}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center gap-4 p-8 lg:p-10">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-soft">
            Biedradar
          </span>
          <h2 className="text-2xl font-bold leading-tight tracking-tight text-ink lg:text-3xl">
            {heading}
          </h2>
          <p className="text-base leading-relaxed text-ink-muted">{text}</p>
          <a
            href={consumerLink("/get-report")}
            className="ring-focus inline-flex w-fit items-center gap-2 rounded-md bg-brand px-6 py-3 text-base font-semibold text-white shadow-soft transition-colors hover:bg-brand-dim"
          >
            {buttonLabel}
            <ArrowRight size={17} />
          </a>
          <p className="text-sm text-ink-faint">{t.note}</p>
        </div>
      </div>
    </aside>
  );
}
